import { useIntl } from 'react-intl';
import { Form, FormInstance, Row, Col, Skeleton, Input } from 'antd';

import {
  useDefaultEmptyFieldError,
  useDefaultMaxLengthFieldError,
  useDefaultMinLengthFieldError,
  useDefaultOnlyLettersRule,
} from '@/hooks';

import { UploadArchiveResult } from '@/core/domain/archive/Archive/Archive.types';

import {
  FloatingFormItem,
  ImagePickerFormField,
  UploadFile,
  InputDecimal,
} from '@/components/_common';
import ManufacturerInfiniteSelectField from '@/components/_common/ManufacturerInfiniteSelectField';
import CfopSelectField from '@/components/_common/CfopSelectField';
import NcmSelectField from '@/components/_common/NcmSelectField';
import GroupsInfiniteSelectField from '@/components/_common/GroupsInfiniteSelectField';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { GroupSelect } from '@/core/domain/operational/Groups/Groups.types';
import { Consumable } from '@/core/domain/operational/Consumables/Consumables.types';
import { useEffect, useState } from 'react';

export interface ConsumableFormData {
  active: boolean;
  barCode: string;
  cfopId: number;
  comment: string;
  id: number;
  imageUrl: UploadFile<UploadArchiveResult>[];
  lifespan: number;
  manufacturerId: number;
  manufacturingCode: string;
  name: string;
  ncmId: number;
  sku: string;
  subGroupId: number;
  unit: string;
  unitAlternative: string;
  unitConversionFactor: number;
}

interface ConsumableFormProps {
  form: FormInstance<ConsumableFormData>;
  isLoading: boolean;
  consumableDetail?: Consumable;
}

function ConsumableForm({
  form,
  isLoading,
  consumableDetail,
}: ConsumableFormProps) {
  const intl = useIntl();
  const defaultEmptyFieldError = useDefaultEmptyFieldError();
  const defaultMinLenghtFieldError = useDefaultMinLengthFieldError();
  const defaultMaxLenghtFieldError = useDefaultMaxLengthFieldError();
  const defaultOnlyLettersRule = useDefaultOnlyLettersRule();

  const [selectedGroup, setSelectedGroup] = useState<GroupSelect[]>([]);

  useEffect(() => {
    if (consumableDetail?.subGroup) {
      setSelectedGroup([
        {
          id: consumableDetail.subGroup?.id,
          name: consumableDetail.subGroup?.name,
          subGroups: [consumableDetail.subGroup],
        },
      ]);
    }
  }, [consumableDetail]);

  return (
    <Form form={form} name="consumableGeneral">
      <Skeleton active loading={isLoading}>
        <Row gutter={[16, 9]}>
          <Col span={24} md={12} xxl={12}>
            <FloatingFormItem
              name="name"
              label={intl.formatMessage({ id: 'parts.entity.name' })}
              rules={[defaultEmptyFieldError, defaultMaxLenghtFieldError(200)]}
            >
              <Input />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} xxl={6}>
            <FloatingFormItem
              name="manufacturerId"
              label={intl.formatMessage({ id: 'parts.entity.manufacturer' })}
              rules={[defaultEmptyFieldError]}
            >
              <ManufacturerInfiniteSelectField queryFilter={{ active: true }} />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} xxl={6}>
            <FloatingFormItem
              name="manufacturingCode"
              label={intl.formatMessage({
                id: 'parts.entity.manufacturerCode',
              })}
              rules={[defaultMaxLenghtFieldError(50)]}
            >
              <Input />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} xxl={8}>
            <FloatingFormItem
              name="cfopId"
              label={intl.formatMessage({ id: 'parts.entity.cfop' })}
            >
              <CfopSelectField />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} xxl={8}>
            <FloatingFormItem
              name="ncmId"
              label={intl.formatMessage({ id: 'parts.entity.ncm' })}
            >
              <NcmSelectField
                disabled={!hasPermissions([Authorities.REGISTER_NCM_VIEW])}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} xxl={8}>
            <FloatingFormItem
              name="subGroupId"
              label={intl.formatMessage({ id: 'parts.entity.subGroup' })}
              rules={[defaultEmptyFieldError]}
            >
              <GroupsInfiniteSelectField
                searchBySubGroup
                selectedGroup={selectedGroup}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} xxl={6}>
            <FloatingFormItem
              name="barCode"
              label={intl.formatMessage({ id: 'parts.entity.barCode' })}
              rules={[
                defaultMinLenghtFieldError(8),
                defaultMaxLenghtFieldError(14),
              ]}
            >
              <Input />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} xxl={6}>
            <FloatingFormItem
              name="sku"
              label={intl.formatMessage({
                id: 'parts.entity.sku',
              })}
              rules={[defaultMaxLenghtFieldError(50)]}
            >
              <Input disabled />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} xxl={6}>
            <FloatingFormItem
              name="unit"
              label={intl.formatMessage({ id: 'parts.entity.unit' })}
              rules={[
                defaultEmptyFieldError,
                defaultMaxLenghtFieldError(5),
                defaultOnlyLettersRule,
              ]}
            >
              <Input maxLength={5} />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} xxl={6}>
            <FloatingFormItem
              name="lifespan"
              label={intl.formatMessage({ id: 'parts.entity.lifeSpan' })}
            >
              <InputDecimal min={0} fractions={0} />
            </FloatingFormItem>
          </Col>

          <Col span={24} xxl={12}>
            <FloatingFormItem
              name="comment"
              label={intl.formatMessage({ id: 'parts.entity.comment' })}
            >
              <Input.TextArea rows={3} style={{ height: '104px' }} />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} xxl={12}>
            <ImagePickerFormField maxFileCount={1} fieldName="imageUrl" />
          </Col>
        </Row>
      </Skeleton>
    </Form>
  );
}

export default ConsumableForm;
